/**
 * Notation-token index — runtime-laag voor `registry/notation-tokens.json`.
 *
 * Tokens zijn de single source of truth voor canvas-render-eigenschappen
 * (fill / stroke / text-color / glyph / shape-hint / label-prefix) per
 * ArchiMate-laag, type, of stereotype. M5-§4: vervangt de eerder inline
 * gehardcodeerde kleuren in `apps/web/src/editor/layerColors.ts` en
 * (later) shape-keuzes in `ConceptNode.tsx`.
 *
 * Resolution-precedence wordt door de caller bepaald via een geordende
 * lijst van candidate-token-ids:
 *
 *   stereotype-token  >  type-override-token  >  layer-token  >  null
 *
 * De web-app bouwt die lijst per concept (rekening houdend met
 * stereotype-extends-chain en attribuut-discriminatoren zoals
 * `c4_level`) en passeert 'm aan `resolveFirst`. Daarmee blijft deze
 * laag dom — geen registry- of pack-koppeling.
 */

export type NotationTokenKind =
  | "stereotype"
  | "stereotype-legacy"
  | "type"
  | "type-legacy"
  | "relation-stereotype"
  | "layer";

export interface NotationToken {
  kind: NotationTokenKind;
  fill?: string;
  stroke?: string;
  stroke_dash?: string;
  text_color?: string;
  glyph?: string;
  label_prefix?: string;
  shape_hint?: string;
}

export interface NotationTokensFile {
  version: string;
  tokens: Record<string, NotationToken>;
}

export class NotationTokensIndex {
  private readonly tokens: Record<string, NotationToken>;

  /**
   * Accepts één file óf een array (last-wins merge). Caller is
   * verantwoordelijk voor merge-volgorde — packs die later komen
   * overschrijven eerdere token-ids.
   */
  constructor(input: NotationTokensFile | NotationTokensFile[]) {
    const files = Array.isArray(input) ? input : [input];
    const merged: Record<string, NotationToken> = {};
    for (const f of files) {
      for (const [id, token] of Object.entries(f.tokens)) {
        merged[id] = token;
      }
    }
    this.tokens = merged;
  }

  get(tokenId: string): NotationToken | undefined {
    return this.tokens[tokenId];
  }

  /** Layer-default voor een ArchiMate-laag-id. */
  resolveLayer(layer: string): NotationToken | undefined {
    return this.tokens[`archimate.layer.${layer}`];
  }

  /** Probeer elk id in volgorde; eerste hit wint. */
  resolveFirst(ids: readonly string[]): NotationToken | undefined {
    for (const id of ids) {
      const t = this.tokens[id];
      if (t) return t;
    }
    return undefined;
  }

  /**
   * Resolve het token voor een concept-base + layer, mét
   * type-override (bv. archimate.Vulnerability heeft een eigen entry
   * los van de Motivation-laag-default). Zonder stereotype-info — de
   * caller doet de stereotype-precedence via `resolveFirst` met een
   * vooraf-gebouwde candidate-lijst.
   */
  resolveForType(typeId: string, layer: string): NotationToken | undefined {
    return this.resolveFirst([typeId, `archimate.layer.${layer}`]);
  }

  /** Alle token-ids — voor doc-generation. */
  ids(): string[] {
    return Object.keys(this.tokens);
  }
}
